import { Key } from "@zapier/design-system";
import { useMemo, useState } from "react";
import uniqBy from "lodash.uniqby";
import { useHydratePendingConnectionShares } from "./useHydratePendingConnectionShares";
import { ConnectionShare } from "./useFormatConnectionShares";
import { createConnectionShareFromId } from "./createConnectionShareFromId";
import { ConnectionShareType } from "../../types";

function filterAllMemberAndTeamShares(shares: ConnectionShare[]) {
  return shares.filter(
    ({ shareType }) => shareType !== "owner" && shareType !== "all",
  );
}

const findShare = (
  id: Key,
  shareType: ConnectionShareType,
  shares: ConnectionShare[],
) => {
  return shares.find(({ id: previousId, shareType: previousShareType }) => {
    return String(previousId) === String(id) && shareType === previousShareType;
  });
};

export default function usePendingConnectionSharesState(
  shares: ConnectionShare[],
) {
  // Connection shares that will be added on save
  const [rawToAdd, setToAdd] = useState<ConnectionShare[]>([]);
  // Connection shares that will be removed on save
  const [toDelete, setToDelete] = useState<ConnectionShare[]>([]);

  const { hydrated: toAdd, isLoading } =
    useHydratePendingConnectionShares(rawToAdd);

  // Fired when new member is added to the list
  const onAdd = (id: Key, shareType: ConnectionShareType) => {
    // if the id is "all", set the toAdd to the account and remove all other shares
    if (id === "all") {
      setToAdd([createConnectionShareFromId(id, shareType)]);
      setToDelete(filterAllMemberAndTeamShares(shares));
      return;
    }
    // Add the share to the toAdd list
    setToAdd((previous) => {
      // Make sure it's not already in the toAdd list
      if (!findShare(id, shareType, previous)) {
        // Build the share object and add it to the list
        const newConnectionShare = createConnectionShareFromId(
          Number(id),
          shareType,
        );
        const newToAdd = [...previous, newConnectionShare];
        // Check to see if we need to remove the "all" share from:
        // pending shares has all/everyone share
        const pendingAllShare = findShare(
          "all",
          ConnectionShareType.All,
          newToAdd,
        );
        // loaded shares has all/everyone share
        const currentAllShare = findShare(
          "all",
          ConnectionShareType.All,
          shares,
        );
        const needToRemoveAll = pendingAllShare || currentAllShare;
        // Only continue if we need to remove the "all" share
        if (needToRemoveAll) {
          // If the "all" share is in the pending list, remove it
          if (pendingAllShare) {
            setToAdd((setToAddData) =>
              setToAddData.filter(
                (share) => !(share.id === "all" && share.shareType === "all"),
              ),
            );

            return newToAdd.filter(
              (share) => !(share.id === "all" && share.shareType === "all"),
            );
          } else {
            // Else, the "all" share was in the current saved list.
            // Move it to pending deletes if it doesn't already exist
            if (!findShare("all", ConnectionShareType.All, toDelete)) {
              setToDelete((previousDelete) => [
                ...previousDelete,
                createConnectionShareFromId("all", "all"),
              ]);
            }
          }
        }

        return newToAdd;
      }
      return previous;
    });

    // Remove the selected share from the toDelete list if it exists
    if (id) {
      setToDelete((setToDeleteData) => {
        return setToDeleteData.filter((share) => {
          return !(share.id === id && share.shareType === shareType);
        });
      });
    }
  };
  // Fired when a member is removed from the list
  const onRemove = (id: Key, shareType: ConnectionShareType) => {
    // Add the share to the toDelete list if it doesn't exist
    setToDelete((setToDeleteData) => {
      // if the share is not in the toDelete list and in the original shares list, add it to the toDelete list
      // do not add to the delete shares that aren't orignally part of the connection when loaded
      const existingShare = findShare(id, shareType, shares);
      // Keep the original share (name, photoUrl, etc.) instead of a blank
      // placeholder, so a failed removal can still be identified by name.
      if (!findShare(id, shareType, setToDeleteData) && existingShare) {
        return [...setToDeleteData, existingShare];
      }
      return setToDeleteData;
    });
    // Remove share from toAdd list if it exists
    setToAdd((setToAddData) => {
      if (findShare(id, shareType, setToAddData)) {
        return setToAddData.filter((share) => {
          return !(
            String(share.id) === String(id) && share.shareType === shareType
          );
        });
      }
      return setToAddData;
    });
  };

  // Takes the list of current shares, adds pending shares, and removes pending deletes
  const sharesToDisplay = useMemo(() => {
    const loadedShares = [...shares];
    // Add all pending shares that aren't already in the list
    toAdd.forEach((shareToAdd) => {
      if (
        !loadedShares.some(
          (object) =>
            object.id === shareToAdd.id &&
            object.shareType === shareToAdd.shareType,
        )
      ) {
        loadedShares.push(shareToAdd as ConnectionShare);
      }
    });
    // Remove all pending deletes from the list
    return loadedShares.filter(
      (sourceObject) =>
        !toDelete.some(
          (filterObject) =>
            String(filterObject.id) === String(sourceObject.id) &&
            filterObject.shareType === sourceObject.shareType,
        ),
    );
  }, [shares, toDelete, toAdd]);

  return {
    onAdd,
    onRemove,
    toAdd: uniqBy(toAdd, "id"), // Filter out all duplicates if any
    toDelete: uniqBy(toDelete, "id"), // Filter out all duplicates if any
    shares: sharesToDisplay,
    isSharesLoading: isLoading,
  };
}
